
import {
  computed,
  defineComponent,
  getCurrentInstance,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { RootTree as RootTreeType } from '@/templates/Tree';
import HeaderComponent from './docs-main/header/Header.vue';
import FooterComponent from './docs-main/Footer.vue';
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue';
import GlobalBus from '@/plugins/GlobalBus';
import { GlobalBusNames, StoreActions, StoreMutations } from '@/templates/DataNames';
import LanguageSelector from '@/components/ui/LanguageSelector.vue';
import { currentInstance, debounce, getSelectedLangName, onScroller } from '@/heplers';
import { computeRootNames } from '@/heplers/searchHelpers';
import ComputeLayoutComponent from '@/components/ComputeLayoutComponent.vue';
import { mobileDetect } from '@/heplers/detectMobile';
import DocsSidebar from '@/components/DocsSidebar.vue';
import { fullContent } from '@/store/profile/actions';
import { useCodeBlocks } from '@/heplers/composables';
import {
  createCodeExamplesObserver,
  destroyCodeExamplesObserver,
} from '@/heplers/codeExampleObserver';
import { CODE_LINK_CLASS } from '@/constants/element-classes';
import { isSdkMethodPage } from '@/heplers/isSdkMethodPage';

const ArticleRating = () => import('@/components/ui/ArticleRating.vue');
const MobileMenu = () => import('./docs-main/MobileMenu.vue');
const CodeRails = () => import('@/components/docs-main/code-rails-view/CodeRails.vue');

interface StoreProxy {
  getters: Record<string, unknown>;
  commit: (mutation: string, payload?: unknown) => void;
  dispatch: (action: string, payload?: unknown) => Promise<unknown>;
  state: Record<string, unknown>;
}

export default defineComponent({
  // Vue 2.7: metaInfo and serverPrefetch are options API; template types from setup() are Vue 2 compatible
  // @ts-expect-error defineComponent overload inference with metaInfo/serverPrefetch
  name: 'Layout',
  components: {
    DocsSidebar,
    CodeRails,
    LanguageSelector,
    HeaderComponent,
    FooterComponent,
    MobileMenu,
    ComputeLayoutComponent,
    Breadcrumbs,
    ArticleRating,
  },
  metaInfo(this: {
    getTitle: string;
    getDescription: string;
    getPageTitle: string;
    jsonLd: unknown;
    robotsNoIndex: boolean;
  }) {
    return {
      title: this.getTitle,
      meta: [
        {
          name: this.getDescription ? 'description' : '',
          content: this.getDescription,
        },
        {
          property: this.getDescription ? 'og:description' : '',
          content: this.getDescription,
        },
        {
          hid: this.getDescription ? 'description' : '',
          name: this.getDescription ? 'description' : '',
          content: this.getDescription,
        },
        {
          property: 'og:title',
          content: this.getPageTitle,
        },
        {
          property: 'og:image:alt',
          content: this.getPageTitle,
        },
        ...(this.robotsNoIndex
          ? [
              {
                vmid: 'robots',
                name: 'robots',
                content: 'noindex, follow',
              },
            ]
          : []),
      ],
      script: [
        {
          type: this.jsonLd ? 'application/ld+json' : '',
          json: this.jsonLd,
        },
      ],
    };
  },
  setup() {
    const instance = getCurrentInstance();
    const store = computed<StoreProxy | undefined>(
      () => instance?.proxy?.$store as StoreProxy | undefined
    );
    const route = computed(() => instance?.proxy?.$route);

    const { setCurrentCodeId } = useCodeBlocks();

    const layoutColumn = ref<HTMLElement | null>(null);
    const breadcrumb = ref<{ $el?: HTMLElement } | null>(null);

    const isShowMobile = ref(false);
    const loadCodeRails = ref(false);
    const windowHeight = ref<number | 'auto' | null>(null);
    const isOpenMobileMenu = ref(false);
    const loadTree = ref(false);
    const rootNames = ref<Record<string, unknown>>({});
    const detectMobile = ref(false);
    const breadcrumbHeight = ref<number | 'auto'>(40);

    const emptyMetaPages = ['references', 'search'];

    const treeData = computed<RootTreeType>(
      () => (store.value?.getters?.getTree as RootTreeType) ?? ({} as RootTreeType)
    );
    const breadcrumbs = computed<{ title: string; url: string }[]>(
      () => (store.value?.getters?.getBreadcrumbs as { title: string; url: string }[]) ?? []
    );
    const data = computed(
      () => store.value?.getters?.getContent as { description?: string; fqdn?: string } | undefined
    );
    const pageIncludesCodeExamples = computed<boolean>(
      () => (store.value?.getters?.pageIncludesCodeExamples as boolean) ?? false
    );
    const selectedLang = computed(() => (store.value?.state?.selectedLang as string) ?? '');
    const toggleListeners = computed(
      () => (store.value?.state?.toggleListeners as boolean) ?? false
    );
    const contentLoading = computed<boolean>(
      () => (store.value?.state?.contentLoading as boolean) ?? false
    );
    const pageRendered = computed<boolean>(
      () => (store.value?.state?.pageRendered as boolean) ?? false
    );
    const isPageReadyForAnchor = computed(() => pageRendered.value && !contentLoading.value);

    const getHashFromLocation = () =>
      (typeof window !== 'undefined' && window.location.hash) || route.value?.hash || '';

    const scrollToHashWhenReady = async () => {
      const hash = getHashFromLocation();
      if (!hash || !isPageReadyForAnchor.value) return;

      await nextTick();
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

      if (typeof document !== 'undefined' && document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }

      await onScroller(hash);
    };

    const minHeight = computed(() => {
      if (windowHeight.value && windowHeight.value !== 'auto') {
        return `${(windowHeight.value as number) - 48}px`;
      }
      return 'auto';
    });

    const getDescription = computed(
      () => (data.value as { description?: string } | undefined)?.description ?? null
    );

    const firstFqdn = computed(() => {
      const fqdn = route.value?.meta?.fqdn;
      if (fqdn) return String(fqdn).split('.')[0];
      return '';
    });

    const getTitle = computed(() => {
      const fqdn = route.value?.meta?.fqdn;
      const splittedFqdn = typeof fqdn === 'string' ? fqdn.split('.') : [];
      let resultTitle = 'Documentation';

      if (emptyMetaPages.includes(firstFqdn.value) && splittedFqdn.length === 1) {
        return route.value?.name as string;
      }

      if (firstFqdn.value === 'references') {
        resultTitle = breadcrumbs.value
          .map((obj) => {
            return obj.title === 'References' ? 'API References' : obj.title.replace(/\s/g, ' ');
          })
          .reverse()
          .join(' | ');
      }

      if (['tutorials', 'introduction', 'howtos'].includes(firstFqdn.value)) {
        resultTitle = breadcrumbs.value
          .map((obj, index) => {
            if (index === 0 || index === breadcrumbs.value.length - 1) {
              return obj.title.replace(/\s/g, ' ');
            }
            return null;
          })
          .filter((elm): elm is string => Boolean(elm))
          .reverse()
          .join(' | ');
      }

      return resultTitle;
    });

    const getPageTitle = computed(
      () =>
        `${getTitle.value} | ${currentInstance === 'kit' ? 'Voximplant Kit | ' : ''}Voximplant.com`
    );

    const currentSdkName = computed(() => getSelectedLangName(route.value?.meta?.fqdn as string));

    const hideLanguageTabs = computed(() => {
      const prohibitedLanguage = ['websdk', 'reactnative', 'voxengine', 'kit', 'fluttersdk'];
      const fqdnParts = String(route.value?.meta?.fqdn || '').split('.');
      const isMainReference = fqdnParts.length < 3 && fqdnParts[0] === 'references';

      return (
        prohibitedLanguage.includes(currentSdkName.value) ||
        isSearchPage.value ||
        pageIncludesCodeExamples.value ||
        isMainReference
      );
    });

    const isSearchPage = computed(() => route.value?.name === 'Search');

    const jsonLdBreadcrumbs = computed(() => {
      const startUrl =
        currentInstance === 'vox' ? 'https://voximplant.com' : 'https://voximplant.com/kit';

      const resultBreadcrumbs = [
        {
          '@type': 'ListItem',
          position: '1',
          name:
            currentInstance === 'vox' ? 'Voximplant Documentation' : 'Voximplant Kit Documentation',
          item: startUrl + '/docs/',
        },
      ];
      breadcrumbs.value.forEach((obj, index) => {
        resultBreadcrumbs.push({
          '@type': 'ListItem',
          position: (index + 2).toString(),
          name: obj.title,
          item: startUrl + obj.url,
        });
      });

      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: resultBreadcrumbs,
      };
    });

    const jsonLdRootNames = computed(() => {
      const fqdn = route.value?.meta?.fqdn as string | undefined;
      const splitFqdn = typeof fqdn === 'string' ? fqdn.split('.') : undefined;

      if (!splitFqdn) return null;

      let resultObj: {
        '@context': string;
        '@type': string;
        url: string;
        potentialAction: { '@type': string; target: string; 'query-input': string };
      } | null = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: '',
        potentialAction: {
          '@type': 'SearchAction',
          target: '',
          'query-input': 'required name=search_term_string',
        },
      };

      const startUrl =
        currentInstance === 'vox' ? 'https://voximplant.com' : 'https://voximplant.com/kit';

      if (splitFqdn[0] === 'references') {
        resultObj.url = startUrl + (route.value?.fullPath || '');

        if (splitFqdn.length === 1) {
          const referenceCats: string = Object.keys(rootNames.value)
            .filter((key) => key.includes('references'))
            .map((key) => 'cats%5B%5D=' + key)
            .join('&');
          resultObj.potentialAction.target = `${startUrl}/docs/search?${referenceCats}&q={search_term_string}`;
        }

        if (splitFqdn.length === 2) {
          resultObj.potentialAction.target = `${startUrl}/docs/search?cats%5B%5D=${fqdn}&q={search_term_string}`;
        }

        if (splitFqdn.length > 2) resultObj.url = '';
      }

      if (
        ['tutorials', 'introduction', 'howtos'].includes(splitFqdn[0]) &&
        splitFqdn.length === 1
      ) {
        resultObj.url = startUrl + (route.value?.fullPath || '');
        resultObj.potentialAction.target = `${startUrl}/docs/search?cats%5B%5D=${fqdn}&q={search_term_string}`;
      }

      if (!resultObj.url.length) resultObj = null;

      return resultObj;
    });

    const jsonLd = computed(() => {
      if (isSearchPage.value) return null;
      if (jsonLdRootNames.value) {
        if (route.value?.meta?.fqdn === 'references') return jsonLdRootNames.value;
        return [jsonLdRootNames.value, jsonLdBreadcrumbs.value];
      }
      return jsonLdBreadcrumbs.value;
    });

    const pageId = computed(() => (route.value?.meta?.fqdn as string) ?? '');

    const robotsNoIndex = computed(() =>
      isSdkMethodPage(route.value?.meta?.fqdn as string | undefined)
    );

    const isReferencesPage = computed(
      () =>
        (currentInstance === 'vox' && route.value?.meta?.fqdn === 'references') ||
        (currentInstance !== 'vox' &&
          (route.value?.meta?.fqdn === 'references' || route.value?.name === 'Voximplant Kit'))
    );

    let debounceHandlerResize: () => void;

    const setBreadcrumbsMutation = (url: string) => {
      store.value?.commit(StoreMutations.BREADCRUMBS, url);
    };
    const updateContentMutation = (content: unknown) => {
      store.value?.commit(StoreMutations.CONTENT, content);
    };
    const setRenderPageStatusMutation = (status: boolean) => {
      store.value?.commit(StoreMutations.SET_RENDER_PAGE_STATUS, status);
    };
    const setMobileVisibleMutation = (isMobile: boolean) => {
      store.value?.commit(StoreMutations.SET_MOBILE_STATE, isMobile);
    };
    const setProgressBarMutation = (progressBar: boolean) => {
      store.value?.commit(StoreMutations.SET_PROGRESSBAR_STATUS, progressBar);
    };
    const setMobileCodeRailsMutation = (bool: boolean) => {
      store.value?.commit(StoreMutations.SET_MOBILE_CODE_RAILS, bool);
    };

    const actionTree = (param: Record<string, unknown>) => {
      return store.value?.dispatch(StoreActions.TREE, param);
    };
    const actionContent = (param: Record<string, unknown>) => {
      return store.value?.dispatch(StoreActions.CONTENT, param);
    };

    const checkMobileResponse = (fromResize?: boolean) => {
      if (typeof window === 'undefined') return;
      windowHeight.value = window.innerHeight;
      const breadcrumbEl = breadcrumb.value?.$el as HTMLElement | undefined;
      breadcrumbHeight.value = breadcrumbEl?.offsetHeight || 40;
      isShowMobile.value = document.body.clientWidth < 1024;
      setMobileCodeRailsMutation(document.body.clientWidth < 1280);
      setMobileVisibleMutation(isShowMobile.value);
      if (!fromResize) addListeners();
    };

    const addListeners = () => {
      window.addEventListener('resize', debounceHandlerResize, { passive: true });
    };

    const removeListeners = () => {
      window.removeEventListener('resize', debounceHandlerResize);
    };

    const getTreeData = async (cookie?: string) => {
      const ssrContext = (instance?.proxy as {
        $ssrContext?: { req?: { headers?: { cookie?: string } } };
      })?.$ssrContext;
      await actionTree({ anchors: 0, cookie, ssrContext })
        .then(() => (loadTree.value = true))
        .catch((error) => error);
    };

    const getContentData = async (cookie?: string) => {
      const fqdn = route.value?.meta?.fqdn as string | undefined;
      const url = fqdn || '';
      const splittedUrl = url.split('.');

      if (isReferencesPage.value) {
        updateContentMutation(fullContent);
        return;
      }

      if (!url.length) return;

      if (typeof window !== 'undefined') {
        const dataFromLanding = localStorage.getItem('fromLanding');
        const contentData = data.value as { fqdn?: string } | undefined;
        if (url === contentData?.fqdn && dataFromLanding) {
          localStorage.removeItem('fromLanding');
          return;
        }
      }

      const ssrContext = (instance?.proxy as { $ssrContext?: unknown })?.$ssrContext;
      const query = route.value?.query as Record<string, string> | undefined;
      const payload: Record<string, unknown> = {
        fqdn: url,
        cookie,
        ssrContext,
        ...query,
      };
      if (splittedUrl.length === 2 && splittedUrl[0] === 'references') {
        payload.depth = '2';
      }
      await actionContent(payload).catch((error) => error);
    };

    const getRootNames = () => {
      const names = computeRootNames(treeData.value);
      rootNames.value = names;
      store.value?.commit(StoreMutations.ROOT_NAMES, names);
    };

    const initLoadingPage = (layoutRef: HTMLElement) => {
      setRenderPageStatusMutation(false);
      setProgressBarMutation(true);
      layoutRef.style.opacity = '0';
      if (!route.value?.hash && route.value?.name !== 'Search' && layoutRef) {
        layoutRef.scrollTo({ top: 0, behavior: 'auto' });
      }
    };

    const finishRenderPage = (layoutRef: HTMLElement) => {
      layoutRef.style.opacity = '1';
      setRenderPageStatusMutation(true);
      setProgressBarMutation(false);
      loadCodeRails.value = true;
    };

    const loadPageContent = async (layoutRef: HTMLElement, fromMount: boolean) => {
      const url = route.value?.meta?.fqdn as string;
      if (!fromMount) await getContentData();
      setBreadcrumbsMutation(url);
      finishRenderPage(layoutRef);
    };

    const fetchItem = async (cookie?: string) => {
      await getTreeData(cookie);
      await getContentData(cookie);
      setBreadcrumbsMutation(route.value?.meta?.fqdn as string);
      getRootNames();
    };

    const renderPage = async (fromMount: boolean) => {
      detectMobile.value = mobileDetect();
      const layoutRef = layoutColumn.value;

      if (!layoutRef) return;

      if (!fromMount) initLoadingPage(layoutRef);

      if (isSearchPage.value) {
        GlobalBus.emit(GlobalBusNames.RENDERSEARCH);
        finishRenderPage(layoutRef);
        return;
      }

      await loadPageContent(layoutRef, fromMount);
    };

    const initUIMethods = () => {
      GlobalBus.on(GlobalBusNames.TOGGLEMOBILE, (value: boolean) => {
        isOpenMobileMenu.value = value;
      });
      checkMobileResponse(false);
    };

    watch(
      () => route.value,
      (newValue, oldValue) => {
        if (!newValue || !oldValue) return;
        if (newValue.meta?.fqdn !== oldValue.meta?.fqdn || isSearchPage.value) {
          renderPage(false);
          return;
        }

        // Same page, only hash changed — content is already ready
        if (newValue.hash && newValue.hash !== oldValue.hash) {
          onScroller(newValue.hash);
        }
      },
      { deep: true }
    );

    // First load / navigation: scroll once when the page is actually ready
    watch(
      isPageReadyForAnchor,
      (ready) => {
        if (ready) scrollToHashWhenReady();
      },
      { immediate: true }
    );

    watch(selectedLang, () => {
      if (Object.keys(fullContent).length) updateContentMutation(fullContent);
    });

    watch(toggleListeners, (bool) => {
      if (bool) addListeners();
      else removeListeners();
    });

    onMounted(async () => {
      debounceHandlerResize = debounce(() => checkMobileResponse(true), 200);

      if (!Object.keys(treeData.value).length) await fetchItem();

      const withByLink = route.value?.query?.with_by_link;
      if (withByLink) {
        store.value?.commit(StoreMutations.SET_WITH_BY_LINK, true);
      }

      await renderPage(true);

      if (!isSearchPage.value) setBreadcrumbsMutation(route.value?.meta?.fqdn as string);

      await nextTick();
      initUIMethods();
      createCodeExamplesObserver((el) => {
        const anchorId = el.target.id;
        const anchorClassList = el.target.classList;
        if (anchorClassList.contains(CODE_LINK_CLASS)) setCurrentCodeId(anchorId);
      });
    });

    onBeforeUnmount(() => {
      GlobalBus.clear.call(GlobalBus);
      window.removeEventListener('resize', debounceHandlerResize);
      updateContentMutation({});
      setBreadcrumbsMutation('');
      destroyCodeExamplesObserver();
    });

    return {
      layoutColumn,
      breadcrumb,
      isShowMobile,
      loadCodeRails,
      isOpenMobileMenu,
      windowHeight,
      breadcrumbHeight,
      minHeight,
      getDescription,
      getTitle,
      getPageTitle,
      robotsNoIndex,
      currentSdkName,
      hideLanguageTabs,
      isSearchPage,
      jsonLd,
      breadcrumbs,
      contentLoading,
      pageId,
      fetchItem,
      pageIncludesCodeExamples,
    };
  },
  serverPrefetch() {
    const proxy = getCurrentInstance()?.proxy as {
      $ssrContext?: { req?: { headers?: { cookie?: string } } };
      $route?: { query?: { with_by_link?: string } };
      fetchItem?: (cookie?: string) => Promise<void>;
    };
    const cookie = proxy?.$ssrContext?.req?.headers?.cookie;
    const queryWithByLink = proxy?.$route?.query?.with_by_link;

    let cookieWithQuery = cookie;
    if (queryWithByLink && typeof queryWithByLink === 'string') {
      const withByLinkCookie = `with_by_link=${encodeURIComponent(queryWithByLink)}`;
      cookieWithQuery = cookie ? `${cookie}; ${withByLinkCookie}` : withByLinkCookie;
    }
    return proxy?.fetchItem?.(cookieWithQuery);
  },
});
